import { useEffect, useState } from 'react';
import _get from 'lodash/get';
import { useDispatch, useSelector } from 'react-redux';
import { PageHeader } from 'ui/pageHeader';
import { FlexSpacer } from 'ui/flexSpacer';
import { DestructiveButton } from 'ui/button';
import { CounterBadge } from 'ui/badge';
import { TabsGroup, IndexTabsList, IndexTabTrigger } from 'ui/tabsGroup';
import { Delete16 } from 'ui/icons/aurora/16px/Delete16';
import { PaginationFooter } from 'ui/paginationFooter';
import { useClientPaginatedState } from 'ui/table/hooks/useClientPaginatedState';
import { Table } from 'ui/table/Table';
import { TableHeader } from 'ui/tableHeader';
import { TableRows } from 'ui/tableRows';
import { InputText } from 'ui/inputs/inputText';
import { selectIsUuidBasedProject } from 'core/userContext';
import {
  deleteAllScheduledMessagesRequest,
  deleteScheduledMessagesRequest,
  fetchScheduledMessagesRequest,
} from '../actions';
import { scheduledMessagesFilterFn } from '../utils';
import { getScheduledMessagesByMedium, isDataFetching, selectAllMessages } from '../selectors';
import { DeleteScheduledMessagesModal } from './DeleteScheduledMessagesModal';
import { createScheduledMessagesColumns } from './ScheduledMessagesColumns';

/**
 * The string data displayed in the component is abstracted
 */
export const scheduledMessagesContent = {
  pageHeader: 'Scheduled messages',
  deleteAll: (medium /*: string */) => `Delete All ${medium} Messages`,
  deleteSelected: (medium /*: string */) => `Delete Selected ${medium} Messages`,
  filterPlaceholder: 'Filter by Campaign / Email / ID...',
};

/**
 * The page header text requires conjugation based on the user's active selection
 */
export const createHeaderMessage = (numberActive /*: number */, medium /*: string */) => {
  const isSingular = numberActive === 1;
  return `There ${isSingular ? 'is' : 'are'} ${numberActive} scheduled ${medium} message${
    isSingular ? '' : 's'
  }.`;
};

const MODALS = {
  DELETE_SELECTED: 'modals/delete-selected',
  DELETE_ALL: 'modals/delete-all',
};

export const ScheduledMessages = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchScheduledMessagesRequest());
  }, [dispatch]);

  /**
   * --Selector Data--
   * msgsByMedium: the user's scheduled messages coming back from Redux
   * isLoading: loading indicator if any data modifying sagas are executing
   */
  const msgsByMedium = useSelector(getScheduledMessagesByMedium);
  const isLoading = useSelector(isDataFetching);
  const allMessages = useSelector(selectAllMessages);
  const isUuidBased = useSelector(selectIsUuidBasedProject);

  /**
   * --State--
   * selectedRowIds: tracks the user's checked row selection
   * currentMedium: Allow for changing of message medium, default to the first option if none selected
   * filter: the current search text in the table filter input
   * orderBy: the column to sort by
   * sortDirection: the direction to sort
   * modal: the data for the modal
   * modal.isOpen: if the modal is open or closed
   * modal.target: which form of the modal is being used
   */
  const [selectedRowIds, selectRowsIds] = useState([]);
  const [currentMedium, selectMedium] = useState('');
  const [modal, setModalState] = useState({
    isOpen: false,
    target: MODALS.DELETE_SELECTED,
  });

  // Build out the selector count data consumed by the table toolbar
  const messageArray = Object.values(msgsByMedium);
  const selectorCounts = messageArray.reduce((acc, val) => {
    acc[val.label] = val.numMessages;
    return acc;
  }, {});

  // the active medium requies a default for the initial page load before a selection
  let activeMedium = currentMedium;

  if (!activeMedium) {
    activeMedium = _get(messageArray[0], 'label', '');
  }

  // Filter down the active rows by message medium and by filter status and sort
  const fullRows = _get(msgsByMedium, [activeMedium, 'messages'], allMessages);

  const {
    currentPage,
    filter,
    itemsPerPage,
    numItems,
    rows,
    setCurrentPage,
    setFilter,
    setItemsPerPage,
    setSortDirection,
    setSortedBy,
    sortDirection,
    sortedBy,
  } = useClientPaginatedState({
    rows: fullRows,
    sortedBy: 'sendAt',
    filterFn: scheduledMessagesFilterFn,
  });

  const scheduledMessagesColumns = createScheduledMessagesColumns(isUuidBased);
  const activeRowIds = rows.map((row) => row.id);
  // Disallow selection of non-active rows (used to sync delete actions to selections)
  const allowedRowIds = selectedRowIds.filter((id) => activeRowIds.includes(id));

  /**
   * --Selection Conditionals --
   * someRowsAreSelected: checks to see if at least one row is selected
   * allRowsAreSelected: checks to see if all the non-hidden rows are selected
   * disableDeleteBtn: disallow partial deletion when no active selection
   * disableDeleteAllBtn: disallow deletion when no rows active
   *
   * Notes: Used to control toggle-all-rows/toggle-remaining-rows/toggle-no-rows logic in table
   */
  const someRowsAreSelected = selectedRowIds.length > 0;
  const allRowsAreSelected =
    someRowsAreSelected && allowedRowIds.length === activeRowIds.length && activeRowIds.length > 0;
  const disableDeleteBtn = allowedRowIds.length < 1 || isLoading;
  const disableDeleteAllBtn = !rows.length || isLoading;

  const onSelectAll = () => {
    if (allRowsAreSelected) {
      selectRowsIds([]);
    } else {
      selectRowsIds([...activeRowIds]);
    }
  };

  const onSelectRow = ({ id }) => {
    if (selectedRowIds.includes(id)) {
      selectRowsIds(selectedRowIds.filter((r) => r !== id));
    } else {
      selectRowsIds([...selectedRowIds, id]);
    }
  };

  const closeModal = () => {
    setModalState({ isOpen: false });
  };

  const deleteMessages = () => {
    dispatch(
      deleteScheduledMessagesRequest({
        messageMedium: activeMedium,
        ids: allowedRowIds,
      }),
    );
  };

  const deleteAllMessages = () => {
    dispatch(
      deleteAllScheduledMessagesRequest({
        messageMedium: activeMedium,
        ids: [], // even though we're deleting everything the API requires an empty array
      }),
    );
  };

  /**
   * The following is logic for controlling the modal
   */
  const isDeleteAllModal = modal.target === MODALS.DELETE_ALL; // which modal is active
  const modalAction = isDeleteAllModal ? deleteAllMessages : deleteMessages; // Determine the proper action to send into the modal component
  const modalMessages = isDeleteAllModal ? fullRows : allowedRowIds; // determine the number of messages to be deleted
  const channels = Object.keys(selectorCounts);

  return (
    <>
      <PageHeader
        pageSubSection={createHeaderMessage(fullRows.length, activeMedium)}
        pageActions={
          <FlexSpacer gap="actionGap">
            <DestructiveButton
              isDisabled={disableDeleteBtn}
              id="deleteMessagesButton"
              onClick={() => {
                setModalState({
                  isOpen: true,
                  target: MODALS.DELETE_SELECTED,
                });
              }}
            >
              <Delete16 />
              {scheduledMessagesContent.deleteSelected(activeMedium)}
            </DestructiveButton>
            <DestructiveButton
              isDisabled={disableDeleteAllBtn}
              id="deleteAllMessagesButton"
              onClick={() => {
                setModalState({
                  isOpen: true,
                  target: MODALS.DELETE_ALL,
                });
              }}
            >
              <Delete16 />
              {scheduledMessagesContent.deleteAll(activeMedium)}
            </DestructiveButton>
          </FlexSpacer>
        }
      >
        {scheduledMessagesContent.pageHeader}
      </PageHeader>
      <TabsGroup defaultValue={channels[0]} onValueChange={(medium) => selectMedium(medium)}>
        <FlexSpacer justifyContent="space-between">
          <IndexTabsList data-test="scheduled-messages-tabs">
            {channels.map((channel) => {
              return (
                <IndexTabTrigger
                  key={channel}
                  value={channel}
                  data-test={`scheduled-messages-tab-${channel}`}
                >
                  <FlexSpacer>
                    {channel}
                    <CounterBadge>{`${selectorCounts[channel]}`}</CounterBadge>
                  </FlexSpacer>
                </IndexTabTrigger>
              );
            })}
          </IndexTabsList>
          <InputText placeholder="Filter by..." value={filter} onChange={(val) => setFilter(val)} />
        </FlexSpacer>
      </TabsGroup>
      <Table>
        <TableHeader
          columns={scheduledMessagesColumns}
          hasCheckbox
          hasCheckboxColumn
          onSelect={onSelectAll}
          checkboxStatus={allRowsAreSelected}
          setSortDirection={setSortDirection}
          setSortedBy={setSortedBy}
          sortDirection={sortDirection}
          sortedBy={sortedBy}
        />
        <TableRows
          columns={scheduledMessagesColumns}
          hasCheckboxColumn
          onSelectRow={onSelectRow}
          rows={rows}
          selectedRows={selectedRowIds}
        />
      </Table>
      {!rows.length && (
        <FlexSpacer
          justifyContent="center"
          padding="cardPadding"
          data-test="scheduled-messages-empty-state"
        >
          No scheduled {activeMedium} messages found.
        </FlexSpacer>
      )}
      <PaginationFooter
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        numItems={numItems}
        onUpdate={({ page, pageSize }) => {
          setCurrentPage(page);
          setItemsPerPage(pageSize);
        }}
      />
      <DeleteScheduledMessagesModal
        closeModal={closeModal}
        medium={activeMedium}
        numMessages={modalMessages.length}
        primaryAction={modalAction}
        isOpen={modal.isOpen}
      />
    </>
  );
};
